import { html, LitElement } from 'lit';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';

import { getPublicFirestoreUrl } from '../../modules/encoding-helpers.js';
import { q1ProjectChipStyles } from './q1-project-chip-styles.js';

export class Q1ProjectChip extends LitElement {
  static styles = [ q1ProjectChipStyles ];

  static properties = {    
    
    /**
     * The project object to display.
     * 
     * Special Properties:
     * - `chipLink`: If included this will replace the detail view as the project link.
     */
    project: { type: Object },

    /** When true, visually indicates a disabled status. */
    disabled: { type: Boolean, reflect: true },

    /**
     * Optionally specify a comma-separated list of extra fields to display after the project name.
     * The fields must be present in the project object.
     * 
     * @example
     * <q1-project-chip extra-fields="key,status_label" .project=${project}></q1-project-chip>
     */
    extraFields: { type: String, attribute: 'extra-fields' },

    /**
     * Optionally specify the target for project link.
     * By default the project link will open in the same tab.
     */
    target: { type: String, reflect: true },

    /**
     * Defaults to false. If false, the project name will be a link to the project detail view.
     * If true, the chip is not clickable: The project name will not be a link.
     */
    noLinks: { type: Boolean, attribute: 'no-links' },
  };

  constructor() {
    super();

    this.project = null;
    
    this.disabled = false;
    this.extraFields = '';
    this.target = '';
    this.noLinks = false;
  }

  /*=== GETTERS ===*/

  get isEmpty() {
    return !this.project || !this.project.key;
  }

  get displayName() {
    return this.project?.name || 'Unknown Project';
  }

  get projectLink() {
    if (this.project?.chipLink) return this.project.chipLink;
    return `projects/${this.project?.key}`;
  }

  get avatarImage() {
    if (
      (typeof this.project?.avatar === 'string')
      && this.project.avatar.startsWith('http')
    ) return this.project.avatar;

    return getPublicFirestoreUrl(this.project?.avatar?.mini?.path);
  }

  /*=== TEMPLATES ===*/

  render() {
    return html`
      <item-wrapper part="base" class=${this.disabled ? 'disabled' : ''}>
        ${this.projectNameTemplate}

        ${this.extraFieldsTemplate}
      </item-wrapper>
    `;
  }

  get projectNameTemplate() {
    if (this.isEmpty) return html`
      <span class="empty-project">Project info missing</span>
    `;

    if (this.noLinks) {
      return html`
        <span class="project-name" part="name">
          <sl-avatar
            label="${this.displayName}"
            image=${this.avatarImage}
            shape="rounded"
          ></sl-avatar>
          <span class="display-name">${this.displayName}</span>
        </span>
      `;
    }
    
    return html`
      <a href=${this.projectLink} class="project-link" part="name" target="${this.target}">
        <sl-avatar
          label="${this.displayName}"
          image=${this.avatarImage}
          shape="rounded"
        ></sl-avatar>
        <span class="display-name">${this.displayName}</span>
      </a>
    `;
  }

  get extraFieldsTemplate() {
    if (this.isEmpty || !this.extraFields?.trim().length) return '';

    const extraFieldValues = this.extraFields.split(',')
      .map(field => field.trim())
      .filter(field => field && this.project[field])
      .map(field => this.project[field]);

    return extraFieldValues.map(fieldValue => html`
      <span class="extra-field" part="extra-field">${fieldValue}</span>
    `);
  }

}

customElements.define('q1-project-chip', Q1ProjectChip);
